import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { logError, getUserFriendlyErrorMessage } from '../utils/errorLogger';

interface LoginForm {
  phone: string;
  password: string;
}

const Login: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [cooldownActive, setCooldownActive] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(10);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { login, user } = useAuth();
  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>();

  // Handle cooldown timer
  useEffect(() => {
    if (cooldownActive && cooldownTime > 0) {
      cooldownTimerRef.current = setTimeout(() => {
        setCooldownTime((prevTime) => prevTime - 1);
      }, 1000);
    } else if (cooldownTime === 0) {
      setCooldownActive(false);
      setCooldownTime(10);
    }

    return () => {
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
    };
  }, [cooldownActive, cooldownTime]);

  if (user) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const onSubmit = async (data: LoginForm) => {
    // Prevent submission during cooldown
    if (cooldownActive) {
      return;
    }

    setIsLoading(true);
    setLoginError(null); // Clear previous errors

    // Declare phoneWithCountryCode in the outer scope
    let phoneWithCountryCode: string = data.phone.startsWith('+265') ? data.phone : `+265${data.phone}`;

    try {
      await login(phoneWithCountryCode, data.password);
    } catch (error: any) {
      // Log detailed error information using our utility
      logError(error, {
        component: 'Login',
        action: 'Authentication',
        additionalInfo: {
          phoneNumber: phoneWithCountryCode?.replace(/\d(?=\d{4})/g, "*"), // Mask most digits for privacy
          attemptTime: new Date().toISOString()
        }
      });

      // Set user-friendly error message
      setLoginError(getUserFriendlyErrorMessage(error));

      // Add specific logging for 503 errors to help identify the root cause
      if (error.response?.status === 503) {
        console.warn('Server Health Check: The API server returned a 503 Service Unavailable error. This might indicate server overload, maintenance, or other backend issues.');
      }

      // Activate cooldown timer for failed login attempts
      // Check if it's an authentication error (401) or invalid credentials
      const isAuthError = error.response?.status === 401 ||
        (error.response?.data?.error &&
          typeof error.response.data.error === 'string' &&
          error.response.data.error.toLowerCase().includes('invalid credential'));

      if (isAuthError) {
        setCooldownActive(true);
        setCooldownTime(10);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen px-4 py-12 bg-gradient-to-br from-blue-50 via-white to-blue-50 sm:px-6 lg:px-8">
      <div className="w-full max-w-lg">
        {/* Login Card */}
        <div className="overflow-hidden bg-white border border-gray-100 shadow-2xl rounded-2xl">
          {/* Logo and Header - Inside the card */}
          <div className="px-8 pt-10 pb-6 text-center">
            <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6">
              <img
                src="/logo.png"
                alt="MASO ESR Logo"
                className="object-contain w-20 h-20 drop-shadow-lg"
              />
            </div>
            <h2 className="mb-3 text-4xl font-bold text-gray-900 lg:text-5xl xl:text-6xl">
              Welcome Back
            </h2>
            <p className="mb-6 text-lg text-gray-600 lg:text-xl xl:text-2xl">
              Sign in to MASO Election Observer Reporting System
            </p>
          </div>

          <div className="px-8 pb-10">
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-5">
                <div>
                  <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <div className="absolute inset-y-0 flex items-center pointer-events-none left-10">
                      <span className="text-sm text-gray-500">+265</span>
                    </div>
                    <input
                      {...register('phone', {
                        required: 'Phone number is required',
                        pattern: {
                          value: /^(\+265)?[0-9]{9}$/,
                          message: 'Invalid phone number format (9 digits required)',
                        },
                      })}
                      type="tel"
                      autoComplete="phone"
                      className={`block w-full pl-20 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${errors.phone ? 'border-red-300 focus:ring-red-500' : ''}`}
                      placeholder="Enter your phone number"
                    />
                  </div>
                  {errors.phone && (
                    <p className="flex items-center mt-2 text-base text-red-600">
                      <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.phone.message}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters',
                        },
                      })}
                      type="password"
                      autoComplete="current-password"
                      className={`block w-full pl-10 pr-3 py-3  border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${errors.password ? 'border-red-300 focus:ring-red-500' : ''}`}
                      placeholder="••••••••"
                    />
                  </div>
                  {errors.password && (
                    <p className="flex items-center mt-2 text-sm text-red-600">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.password.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Display login error message */}
              {loginError && (
                <div className="p-3 mb-4 border border-red-200 rounded-lg bg-red-50">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Authentication Error</h3>
                      <div className="mt-1 text-sm text-red-700">
                        {loginError}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <LoadingSpinner size="small" />
                      <span className="ml-2">Signing in...</span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      Sign in to Dashboard
                    </div>
                  )}
                </button>
              </div>
            </form>
          </div>


        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            © 2024 MASO Election Observer Reporting System. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;